import { CalendarRange, ListChecks, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import {
  RiskSelectionMode,
  useRiskSelection,
} from '@/sections/export/hooks/useRiskSelection';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import {
  getVariantForStatus,
  vulnerabilityStatusVariants,
} from '@/utils/statusChip.util';

interface RiskSelectionStepProps {
  /**
   * The hook instance, owned by the parent wizard so `resolvedRiskKeys` is
   * available at submit time.
   */
  selection: ReturnType<typeof useRiskSelection>;
}

const MODE_OPTIONS: ReadonlyArray<{
  value: RiskSelectionMode;
  label: string;
  sublabel: string;
  icon: typeof ShieldCheck;
}> = [
  {
    value: 'all',
    label: 'All open risks',
    sublabel: 'Cover every currently open risk in this export.',
    icon: ShieldCheck,
  },
  {
    value: 'dateRange',
    label: 'By date range',
    sublabel: 'Cover open risks discovered or last seen within a window.',
    icon: CalendarRange,
  },
  {
    value: 'specific',
    label: 'Specific vulnerabilities',
    sublabel: 'Hand-pick the exact risks this export should cover.',
    icon: ListChecks,
  },
];

/** StatusChip pill for a risk's status code (e.g. `OC` → "Open"). */
function RiskStatusChip({ status }: { status: string }) {
  const { statusLabel } = getRiskStatusLabel(status);
  const label = statusLabel || 'Unknown';
  const variant = getVariantForStatus(label, vulnerabilityStatusVariants);
  return <StatusChip label={label} variant={variant} />;
}

export function RiskSelectionStep({ selection }: RiskSelectionStepProps) {
  const {
    mode,
    setMode,
    dateField,
    setDateField,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedKeys,
    setSelectedKeys,
    resolvedRiskKeys,
    isResolving,
    riskOptions,
    isLoadingOptions,
  } = selection;

  const [search, setSearch] = useState('');

  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? riskOptions.filter(
        opt =>
          opt.label.toLowerCase().includes(normalizedSearch) ||
          opt.key.toLowerCase().includes(normalizedSearch)
      )
    : riskOptions;

  const toggleKey = (key: string) => {
    setSelectedKeys(
      selectedKeys.includes(key)
        ? selectedKeys.filter(k => k !== key)
        : [...selectedKeys, key]
    );
  };

  const matchedCount = resolvedRiskKeys?.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div
        role="radiogroup"
        aria-label="Which risks should this export cover?"
        className="flex flex-col gap-3"
      >
        {MODE_OPTIONS.map(opt => {
          const isSelected = mode === opt.value;
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => setMode(opt.value)}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
                isSelected
                  ? 'border-chariot-brand-primary bg-chariot-background-secondary'
                  : 'border-chariot-stroke hover:bg-chariot-background-secondary'
              )}
            >
              <Icon
                className={cn(
                  'mt-0.5 size-5 shrink-0',
                  isSelected
                    ? 'text-chariot-brand-primary'
                    : 'text-chariot-text-secondary'
                )}
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-chariot-text-primary text-sm font-medium">
                  {opt.label}
                </span>
                <span className="text-chariot-text-secondary text-xs">
                  {opt.sublabel}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {mode === 'dateRange' && (
        <div className="border-chariot-stroke flex flex-col gap-4 rounded-lg border p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="risk-date-field">Date field</Label>
            <Select
              value={dateField}
              onValueChange={value =>
                setDateField(value as 'created' | 'visited')
              }
            >
              <SelectTrigger id="risk-date-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created">Created (first seen)</SelectItem>
                <SelectItem value="visited">Last seen</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="risk-start-date">Start date</Label>
              <Input
                id="risk-start-date"
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="risk-end-date">End date</Label>
              <Input
                id="risk-end-date"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <p className="text-chariot-text-secondary text-xs" aria-live="polite">
            {!startDate || !endDate
              ? 'Pick a start and end date to count matching risks.'
              : isResolving
                ? 'Counting matching risks…'
                : `${matchedCount} open risk${matchedCount === 1 ? '' : 's'} match this range.`}
          </p>
        </div>
      )}

      {mode === 'specific' && (
        <div className="border-chariot-stroke flex flex-col gap-3 rounded-lg border p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="risk-search">Search open risks</Label>
            <Input
              id="risk-search"
              type="text"
              placeholder="Filter by name or key…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <ScrollArea className="border-chariot-stroke h-64 rounded-md border">
            <ul className="flex flex-col p-1">
              {isLoadingOptions ? (
                <li className="text-chariot-text-secondary px-2 py-6 text-center text-sm">
                  Loading open risks…
                </li>
              ) : filteredOptions.length === 0 ? (
                <li className="text-chariot-text-secondary px-2 py-6 text-center text-sm">
                  {riskOptions.length === 0
                    ? 'No open risks found.'
                    : 'No risks match your search.'}
                </li>
              ) : (
                filteredOptions.map(opt => {
                  const checkboxId = `risk-option-${opt.key}`;
                  const isChecked = selectedKeys.includes(opt.key);
                  return (
                    <li key={opt.key}>
                      <label
                        htmlFor={checkboxId}
                        className="hover:bg-chariot-background-secondary flex cursor-pointer items-center gap-3 rounded-xs px-2 py-2"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isChecked}
                          onCheckedChange={() => toggleKey(opt.key)}
                        />
                        <span className="text-chariot-text-primary min-w-0 flex-1 truncate text-sm">
                          {opt.label}
                        </span>
                        <RiskStatusChip status={opt.status} />
                      </label>
                    </li>
                  );
                })
              )}
            </ul>
          </ScrollArea>

          <p className="text-chariot-text-secondary text-xs" aria-live="polite">
            {selectedKeys.length === 0
              ? 'No risks selected — the export will cover all open risks.'
              : `${selectedKeys.length} risk${selectedKeys.length === 1 ? '' : 's'} selected.`}
          </p>
        </div>
      )}
    </div>
  );
}
